import { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { logoDark, logoLight } from '@react-apps/design-system';
import { authContainer } from '../../di/container';

type Status = 'loading' | 'success' | 'collision' | 'invalid' | 'error';

function statusForError(err: unknown): Status {
  const code =
    typeof err === 'object' && err !== null && 'response' in err
      ? (err as { response?: { data?: { error?: string } } }).response?.data
          ?.error
      : undefined;
  if (code === 'EMAIL_ALREADY_IN_USE') return 'collision';
  if (
    code === 'ACTION_TOKEN_NOT_FOUND' ||
    code === 'TOKEN_EXPIRED' ||
    code === 'TOKEN_ALREADY_USED'
  ) {
    return 'invalid';
  }
  return 'error';
}

/**
 * Ato 3 da troca de e-mail (rota pública): a pessoa abriu o link do e-mail NOVO
 * (token CONFIRM). Dispara o `confirm` ao montar. Não há re-login — a tela manda
 * ao `/sign-in` com o e-mail novo (§3.11).
 */
export function ConfirmEmailChangePage() {
  const [searchParams] = useSearchParams();
  /**
   * O token vem na query do link do e-mail. Ele é capturado uma vez no
   * primeiro render e some da barra de endereços em seguida: enquanto está lá,
   * entra no histórico do navegador e no `Referer` de qualquer recurso externo
   * carregado pela página. Mesma mitigação do `PasswordResetCard`.
   */
  const [token] = useState(() => searchParams.get('token'));

  useEffect(() => {
    if (window.location.search) {
      window.history.replaceState(null, '', window.location.pathname);
    }
  }, []);
  const [status, setStatus] = useState<Status>('loading');
  const started = useRef(false);

  const [isDark, setIsDark] = useState(() => {
    if (typeof window === 'undefined') return false;
    return document.documentElement.classList.contains('dark');
  });
  useEffect(() => {
    const observer = new MutationObserver(() =>
      setIsDark(document.documentElement.classList.contains('dark')),
    );
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class'],
    });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    if (!token) {
      setStatus('invalid');
      return;
    }
    authContainer.repository
      .confirmEmailChange(token)
      .then(() => setStatus('success'))
      .catch((err: unknown) => setStatus(statusForError(err)));
  }, [token]);

  return (
    <div className="bg-(--color-bg-secondary) flex min-h-screen flex-col items-center justify-center px-4">
      <div className="flex w-full max-w-sm flex-1 flex-col items-center justify-center">
        <div className="mb-8">
          <img
            src={isDark ? logoDark : logoLight}
            alt="Primum"
            className="h-10 w-auto"
          />
        </div>

        <div className="border-(--color-border) bg-(--color-bg-primary) w-full rounded-2xl border p-8 shadow-sm">
          {status === 'loading' && (
            <p className="text-(--color-text-secondary) text-center text-sm">
              Confirmando a troca do seu e-mail…
            </p>
          )}

          {status === 'success' && (
            <div className="space-y-6">
              <div className="rounded-lg border border-green-200 bg-green-50 px-4 py-6 text-center dark:border-green-900 dark:bg-green-950">
                <p className="text-sm font-semibold text-green-700 dark:text-green-400">
                  E-mail alterado!
                </p>
                <p className="mt-2 text-sm text-green-700 dark:text-green-400">
                  Entre novamente com o <strong>novo e-mail</strong> e a mesma
                  senha. Suas outras sessões foram encerradas.
                </p>
              </div>
              <Link
                to="/sign-in"
                className="bg-(--color-accent) flex w-full items-center justify-center rounded-lg px-4 py-2.5 text-sm font-semibold text-white transition hover:opacity-90"
              >
                Ir para o login
              </Link>
            </div>
          )}

          {(status === 'collision' ||
            status === 'invalid' ||
            status === 'error') && (
            <div className="space-y-6">
              <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-6 text-center dark:border-red-900 dark:bg-red-950">
                <p className="text-sm font-medium text-red-700 dark:text-red-400">
                  {status === 'collision' &&
                    'Já existe uma conta com esse e-mail. Procure o suporte.'}
                  {status === 'invalid' &&
                    'Este link é inválido ou já expirou. Peça a troca de novo.'}
                  {status === 'error' &&
                    'Não foi possível concluir a troca. Tente novamente.'}
                </p>
              </div>
              <Link
                to="/sign-in"
                className="bg-(--color-accent) flex w-full items-center justify-center rounded-lg px-4 py-2.5 text-sm font-semibold text-white transition hover:opacity-90"
              >
                Voltar para o login
              </Link>
            </div>
          )}
        </div>
      </div>

      <footer className="py-6">
        <p className="text-(--color-text-secondary) text-xs">
          © 2026 Grupo Primum. Todos os direitos reservados.
        </p>
      </footer>
    </div>
  );
}
